import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Clock, Sparkles } from "lucide-react";
import { ARTICLES, getArticle, type Article } from "@/data/resources";
import { ogImageMeta } from "@/lib/og-image";
import { trackApplyClick } from "@/lib/apply-cta";

export const Route = createFileRoute("/resources/$slug")({
  loader: ({ params }) => {
    const article = getArticle(params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { article } = loaderData;
    const url = `https://vendingmachinefree.com/resources/${article.slug}`;
    return {
      meta: [
        { title: `${article.title} | Vending Machine Free` },
        { name: "description", content: article.description },
        { property: "og:title", content: article.title },
        { property: "og:description", content: article.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: article.published },
        { name: "twitter:title", content: article.title },
        { name: "twitter:description", content: article.description },
        ...ogImageMeta(),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Article",
              headline: article.title,
              description: article.description,
              datePublished: article.published,
              author: { "@type": "Organization", name: "Vending Machine Free.com" },
              publisher: { "@type": "Organization", name: "Vending Machine Free.com" },
              mainEntityOfPage: url,
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://vendingmachinefree.com/" },
                { "@type": "ListItem", position: 2, name: "Resources", item: "https://vendingmachinefree.com/resources" },
                { "@type": "ListItem", position: 3, name: article.title, item: url },
              ],
            },
          ]),
        },
      ],
    };
  },
  component: ArticlePage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <Link to="/resources" className="text-safety">Back to resources</Link>
    </div>
  ),
  errorComponent: () => <div className="p-10">Error.</div>,
});

function ArticlePage() {
  const { article } = Route.useLoaderData();
  const others = ARTICLES.filter((a) => a.slug !== article.slug);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link to="/" hash="contact" onClick={() => trackApplyClick(`article_${article.slug}_header`)} className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground">
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <article className="py-14">
        <div className="container-x max-w-3xl">
          <nav className="text-xs text-muted-foreground mb-4">
            <Link to="/" className="hover:text-safety">Home</Link> /{" "}
            <Link to="/resources" className="hover:text-safety">Resources</Link> /{" "}
            <span className="text-foreground">{article.category}</span>
          </nav>
          <div className="text-xs uppercase tracking-wider text-safety font-bold">{article.category}</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl uppercase">{article.title}</h1>
          <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {article.readMinutes} min read</span>
            <span>Published {new Date(article.published).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
          </div>

          <p className="mt-8 text-lg leading-relaxed text-muted-foreground">{article.intro}</p>

          {article.sections.map((s: { h2: string; body: string[] }) => (
            <section key={s.h2} className="mt-10">
              <h2 className="font-display text-2xl uppercase">{s.h2}</h2>
              <div className="mt-3 space-y-4 text-base leading-relaxed">
                {s.body.map((p: string, i: number) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>
          ))}

          <aside className="mt-12 rounded-xl border border-border bg-secondary p-6">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-safety font-bold">
              <Sparkles className="h-4 w-4" /> Key Takeaways
            </div>
            <ul className="mt-3 space-y-2">
              {article.keyTakeaways.map((k: string) => (
                <li key={k} className="flex gap-2 text-sm">
                  <span className="text-safety font-bold">→</span>
                  <span>{k}</span>
                </li>
              ))}
            </ul>
          </aside>

          <div className="mt-12 rounded-xl bg-charcoal text-white p-8 text-center">
            <h3 className="font-display text-2xl uppercase">{article.cta}</h3>
            <Link
              to="/"
              hash="contact"
              onClick={() => trackApplyClick(`article_${article.slug}_footer`)}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground"
            >
              Apply Free <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </article>

      {others.length > 0 && (
        <section className="py-14 border-t border-border">
          <div className="container-x max-w-5xl">
            <h3 className="font-display text-xl uppercase text-center">More Guides</h3>
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              {others.map((a: Article) => (
                <Link
                  key={a.slug}
                  to="/resources/$slug"
                  params={{ slug: a.slug }}
                  className="group rounded-lg border border-border bg-card p-5 hover:border-safety transition"
                >
                  <div className="text-xs uppercase tracking-wider text-safety font-bold">{a.category}</div>
                  <div className="mt-1 font-semibold group-hover:text-safety">{a.title}</div>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{a.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
